/**
 * Occasion switcher (sections/occasion-switcher.liquid): tabs (pills on
 * mobile, a list on desktop) switch a stage of photos or videos.
 * - Autoplay moves on when the active progress bar's CSS animation ends, so
 *   pausing the animation pauses the slideshow. It pauses off screen, in a
 *   hidden tab, with the pause button, while an occasion is selected in the
 *   theme editor, and never runs for "reduce motion".
 * - Swipe the stage left or right to change occasion; arrow keys, Home and
 *   End move between tabs.
 * - Only the active occasion's video plays.
 * - Photos start loading when the section is within about one and a half
 *   screens (the browser's own lazy loading waits until they're nearly on
 *   screen): the first occasion's, then the rest, which are rendered
 *   without a src so they can't hold the first one up. One that's chosen
 *   before then loads straight away.
 */
class OccasionSwitcher extends HTMLElement {
  connectedCallback() {
    this.tabList = this.querySelector('[data-tabs]');
    this.tabs = [...this.querySelectorAll('[role="tab"]')];
    this.slides = [...this.querySelectorAll('[role="tabpanel"]')];
    this.segments = [...this.querySelectorAll('.occasion-switcher__segment')];
    this.stage = this.querySelector('[data-stage]');
    this.pauseBtn = this.querySelector('[data-pause]');
    this.index = 0;
    this.pauseReasons = new Set();

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.autoplay = this.dataset.autoplay === 'true' && this.slides.length > 1 && !reducedMotion;
    if (!this.autoplay) {
      this.classList.add('no-autoplay');
      if (this.pauseBtn) this.pauseBtn.hidden = true;
    }

    this.listeners = [
      [this.tabList, 'click', (evt) => this.handleTabClick(evt)],
      [this.tabList, 'keydown', (evt) => this.handleTabKeydown(evt)],
      [this, 'animationend', (evt) => this.handleAnimationEnd(evt)],
      [this.stage, 'pointerdown', (evt) => this.handlePointerDown(evt)],
      [this.stage, 'pointerup', (evt) => this.handlePointerUp(evt)],
      [this.stage, 'pointercancel', () => { this.pointer = null; }],
      [document, 'visibilitychange', () => this.setPaused('hidden', document.hidden)],
      // Theme editor: selecting an occasion shows it and holds it there.
      [this, 'shopify:block:select', (evt) => this.handleBlockSelect(evt, true)],
      [this, 'shopify:block:deselect', (evt) => this.handleBlockSelect(evt, false)]
    ];
    if (this.pauseBtn) {
      this.listeners.push([this.pauseBtn, 'click', () => this.setPaused('user', !this.pauseReasons.has('user'))]);
    }
    this.listeners.forEach(([target, type, handler]) => target.addEventListener(type, handler));

    this.visibilityObserver = new IntersectionObserver(([entry]) => {
      this.setPaused('offscreen', !entry.isIntersecting || entry.intersectionRatio < 0.25);
    }, { threshold: [0, 0.25] });
    this.visibilityObserver.observe(this.stage);

    this.nearObserver = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting) return;
      this.nearObserver.disconnect();
      this.loadImages();
    }, { rootMargin: '150% 0px' });
    this.nearObserver.observe(this.stage);

    this.setPaused('offscreen', true);
  }

  /**
   * Load the first occasion's photo now, then the others once it's in.
   */
  loadImages() {
    const first = this.slides[0].querySelector('img');
    const loadRest = () => this.slides.forEach((slide) => OccasionSwitcher.loadImage(slide));

    if (!first || first.hasAttribute('data-manual-src')) {
      loadRest();
      return;
    }
    first.loading = 'eager';
    if (first.complete) {
      loadRest();
    } else {
      first.addEventListener('load', loadRest, { once: true });
      first.addEventListener('error', loadRest, { once: true });
    }
  }

  /**
   * Load an occasion's photo now, if it's waiting.
   * @param {Element} slide - The occasion's slide.
   */
  static loadImage(slide) {
    const img = slide.querySelector('img');
    if (!img) return;
    img.loading = 'eager';
    if (img.dataset.manualSrcset) {
      img.srcset = img.dataset.manualSrcset;
      img.removeAttribute('data-manual-srcset');
    }
    if (img.dataset.manualSrc) {
      img.src = img.dataset.manualSrc;
      img.removeAttribute('data-manual-src');
    }
  }

  disconnectedCallback() {
    this.listeners.forEach(([target, type, handler]) => target.removeEventListener(type, handler));
    this.visibilityObserver.disconnect();
    this.nearObserver.disconnect();
  }

  /**
   * Show an occasion.
   * @param {number} index - Occasion index (wraps around).
   * @param {boolean} [focusTab=false] - Move focus to its tab (keyboard).
   */
  goTo(index, focusTab = false) {
    const count = this.slides.length;
    const next = ((index % count) + count) % count;
    if (next === this.index) return;
    this.index = next;

    this.tabs.forEach((tab, i) => {
      const active = i === next;
      tab.classList.toggle('is-active', active);
      tab.setAttribute('aria-selected', active ? 'true' : 'false');
      tab.tabIndex = active ? 0 : -1;
    });
    this.slides.forEach((slide, i) => {
      const active = i === next;
      slide.classList.toggle('is-active', active);
      if (active) {
        slide.removeAttribute('aria-hidden');
      } else {
        slide.setAttribute('aria-hidden', 'true');
      }
    });
    this.segments.forEach((segment, i) => {
      segment.classList.toggle('is-done', i < next);
      segment.classList.toggle('is-active', i === next);
    });

    OccasionSwitcher.loadImage(this.slides[next]);

    const tab = this.tabs[next];
    if (focusTab) tab.focus({ preventScroll: true });
    this.scrollTabIntoView(tab);
    this.syncVideos();
  }

  /**
   * Keep the active pill in view in the scrolling pill row (mobile), without
   * moving the page.
   * @param {Element} tab - The active tab.
   */
  scrollTabIntoView(tab) {
    const list = this.tabList;
    if (list.scrollWidth <= list.clientWidth) return;
    const left = tab.offsetLeft - (list.clientWidth - tab.offsetWidth) / 2;
    list.scrollTo({ left, behavior: 'smooth' });
  }

  /**
   * Pause or resume for a reason; the slideshow runs only with none left.
   * @param {string} reason - 'user', 'offscreen', 'hidden' or 'editor'.
   * @param {boolean} paused - Whether that reason applies.
   */
  setPaused(reason, paused) {
    if (paused) {
      this.pauseReasons.add(reason);
    } else {
      this.pauseReasons.delete(reason);
    }
    this.classList.toggle('is-paused', this.pauseReasons.size > 0);
    this.classList.toggle('is-user-paused', this.pauseReasons.has('user'));
    if (this.pauseBtn) {
      const userPaused = this.pauseReasons.has('user');
      this.pauseBtn.setAttribute('aria-label', userPaused ? this.pauseBtn.dataset.labelPlay : this.pauseBtn.dataset.labelPause);
    }
    this.syncVideos();
  }

  /**
   * Play the active occasion's video (when nothing has paused the section)
   * and pause the rest.
   */
  syncVideos() {
    const running = !this.pauseReasons.has('offscreen') && !this.pauseReasons.has('hidden') && !this.pauseReasons.has('user');
    this.slides.forEach((slide, i) => {
      const video = slide.querySelector('video');
      if (!video) return;
      if (i === this.index && running) {
        const playing = video.play();
        if (playing) playing.catch(() => {});
      } else {
        video.pause();
      }
    });
  }

  /**
   * @param {object} evt - Click event.
   */
  handleTabClick(evt) {
    const tab = evt.target.closest('[role="tab"]');
    if (tab) this.goTo(this.tabs.indexOf(tab));
  }

  /**
   * Arrow keys, Home and End move between tabs (and show them).
   * @param {object} evt - Keydown event.
   */
  handleTabKeydown(evt) {
    const rtl = getComputedStyle(this).direction === 'rtl';
    const keys = {
      ArrowRight: rtl ? -1 : 1,
      ArrowDown: 1,
      ArrowLeft: rtl ? 1 : -1,
      ArrowUp: -1
    };
    let target;
    if (evt.key in keys) target = this.index + keys[evt.key];
    if (evt.key === 'Home') target = 0;
    if (evt.key === 'End') target = this.tabs.length - 1;
    if (target === undefined) return;
    evt.preventDefault();
    this.goTo(target, true);
  }

  /**
   * Autoplay: move on when the active progress bar fills.
   * @param {object} evt - Animationend event.
   */
  handleAnimationEnd(evt) {
    if (evt.animationName !== 'occasion-progress' || !this.autoplay) return;
    if (!evt.target.closest('.is-active')) return;
    this.goTo(this.index + 1);
  }

  /**
   * @param {object} evt - Pointerdown event.
   */
  handlePointerDown(evt) {
    if (evt.target.closest('[data-pause]')) return;
    this.pointer = { id: evt.pointerId, x: evt.clientX, y: evt.clientY };
  }

  /**
   * A mostly horizontal swipe of 40px or more changes occasion.
   * @param {object} evt - Pointerup event.
   */
  handlePointerUp(evt) {
    const start = this.pointer;
    this.pointer = null;
    if (!start || start.id !== evt.pointerId) return;
    const dx = evt.clientX - start.x;
    const dy = evt.clientY - start.y;
    if (Math.abs(dx) < 40 || Math.abs(dx) < Math.abs(dy) * 1.2) return;
    const rtl = getComputedStyle(this).direction === 'rtl';
    const step = (dx < 0) !== rtl ? 1 : -1;
    this.goTo(this.index + step);
  }

  /**
   * @param {object} evt - shopify:block:select / deselect event.
   * @param {boolean} selected - Whether the block was selected.
   */
  handleBlockSelect(evt, selected) {
    const tab = evt.target.closest('[role="tab"]');
    if (!tab) return;
    if (selected) this.goTo(this.tabs.indexOf(tab));
    this.setPaused('editor', selected);
  }
}

if (!customElements.get('occasion-switcher')) {
  customElements.define('occasion-switcher', OccasionSwitcher);
}
